import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { assignSubscription, getUserSubscriptionDetails, UserSubscriptionDetails } from "@/services/admin/adminSubscriptionService";
import { SubscriptionTier } from "@/services/subscriptions/types";
import { getTierDisplayName } from "@/services/subscriptions/access";
import { AlertCircle, Calendar, Shield } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

interface UserSubscriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userName: string;
  currentTier?: SubscriptionTier;
  onSuccess?: () => void;
}

const TIER_LIMITS = {
  free: 20,
  starter: 100,
  pro: 800,
  unlimited: Infinity
};

export function UserSubscriptionDialog({
  open,
  onOpenChange,
  userId,
  userName,
  currentTier = 'free',
  onSuccess
}: UserSubscriptionDialogProps) {
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier>(currentTier);
  const [reason, setReason] = useState("");
  const [durationDays, setDurationDays] = useState(30);
  const [isLoading, setIsLoading] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState<UserSubscriptionDetails | null>(null);

  const handleOpenChange = async (newOpen: boolean) => {
    if (newOpen) {
      setIsLoading(true);
      try {
        const details = await getUserSubscriptionDetails(userId);
        setSubscriptionDetails(details);
        setSelectedTier(details?.subscription?.tier || currentTier);
        setReason("");
        setDurationDays(30);
      } catch (error) {
        console.error('Error loading subscription details:', error);
        toast({
          title: "Error Loading Subscription",
          description: "Failed to load subscription details. Please try again.",
          variant: "destructive"
        });
        onOpenChange(false);
        return;
      } finally {
        setIsLoading(false);
      }
    } else {
      setReason("");
      setDurationDays(30);
      setIsLoading(false);
    }
    onOpenChange(newOpen);
  };

  const handleAssignSubscription = async () => {
    if (!reason.trim()) {
      toast({
        title: "Reason Required",
        description: "Please provide a reason for this subscription change.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      await assignSubscription(userId, selectedTier, reason, durationDays);
      
      toast({
        title: "Subscription Updated",
        description: `${userName}'s subscription has been changed to ${getTierDisplayName(selectedTier)}.`
      });

      onSuccess?.();
      onOpenChange(false);
      setReason("");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update subscription",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const currentLimit = TIER_LIMITS[currentTier];
  const newLimit = TIER_LIMITS[selectedTier];
  const currentUsage = subscriptionDetails?.lead_imports || 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden sm:max-w-[640px] mx-4">
        <DialogHeader>
          <DialogTitle>Manage Subscription: {userName}</DialogTitle>
          <DialogDescription>
            Update subscription tier and settings for this user
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-5 overflow-y-auto max-h-[calc(90vh-200px)]">
          <div className="space-y-6">
          {/* Current Status */}
          <div className="rounded-lg border border-border/60 p-4 bg-muted/30">
            <div className="flex items-start justify-between gap-3 mb-3">
              <h3 className="font-semibold flex items-center gap-2 flex-1">
                <Shield className="h-4 w-4 shrink-0" />
                Current Subscription
              </h3>
              <Badge 
                variant={
                  currentTier === 'free' ? 'outline' :
                  currentTier === 'starter' ? 'info' :
                  currentTier === 'pro' ? 'default' :
                  'gradient'
                }
                className="capitalize shrink-0"
              >
                {getTierDisplayName(currentTier)}
              </Badge>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm">
              <div>
                <p className="text-muted-foreground mb-1 text-xs">Leads This Month</p>
                <p className="font-medium">{currentUsage} / {currentLimit === Infinity ? '∞' : currentLimit}</p>
              </div>
              {subscriptionDetails?.subscription?.current_period_end && (
                <div>
                  <p className="text-muted-foreground mb-1 text-xs">Expires</p>
                  <p className="font-medium flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {format(new Date(subscriptionDetails.subscription.current_period_end), 'MMM d, yyyy')}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* New Tier Selection */}
          <div className="space-y-2">
            <Label htmlFor="tier" className="text-sm font-medium">
              New Subscription Tier
            </Label>
            <Select value={selectedTier} onValueChange={(value) => setSelectedTier(value as SubscriptionTier)}>
              <SelectTrigger id="tier">
                <SelectValue placeholder="Select subscription tier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="free">Free (20 leads/month)</SelectItem>
                <SelectItem value="starter">Starter (100 leads/month)</SelectItem>
                <SelectItem value="pro">Pro (800 leads/month)</SelectItem>
                <SelectItem value="unlimited">Unlimited (∞ leads)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Duration */}
          <div className="space-y-2">
            <Label htmlFor="duration" className="text-sm font-medium">
              Subscription Duration
            </Label>
            <Select value={durationDays.toString()} onValueChange={(value) => setDurationDays(Number(value))}>
              <SelectTrigger id="duration">
                <SelectValue placeholder="Select duration" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 days</SelectItem>
                <SelectItem value="30">30 days (1 month)</SelectItem>
                <SelectItem value="90">90 days (3 months)</SelectItem>
                <SelectItem value="180">180 days (6 months)</SelectItem>
                <SelectItem value="365">365 days (1 year)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason" className="text-sm font-medium">
              Reason for Change <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="reason"
              placeholder="E.g., Customer request, promotional offer, support escalation..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              className={cn(
                "resize-none",
                reason.trim() && "border-green-500/50 focus-visible:ring-green-500/20"
              )}
            />
            {!reason.trim() ? (
              <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-1.5">
                <span className="text-base leading-none">●</span>
                Required field - please explain the reason for this change
              </p>
            ) : (
              <p className="text-xs text-green-600 dark:text-green-500 flex items-center gap-1.5 mt-1.5">
                <span className="text-base leading-none">✓</span>
                Reason provided
              </p>
            )}
          </div>

          {/* Preview Changes */}
          {selectedTier !== currentTier && (
            <div className="rounded-lg border border-blue-500/40 bg-blue-500/5 p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="rounded-full bg-blue-500/10 p-1.5 shrink-0">
                  <AlertCircle className="h-4 w-4 text-blue-600 shrink-0" />
                </div>
                <div className="space-y-2 flex-1">
                  <p className="font-semibold text-sm text-blue-900 dark:text-blue-100">Preview Changes</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground mb-1 text-xs">Tier Change</p>
                      <p className="font-medium">
                        {getTierDisplayName(currentTier)} → {getTierDisplayName(selectedTier)}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1 text-xs">New Limit</p>
                      <p className="font-medium">
                        {newLimit === Infinity ? '∞' : newLimit} leads/month
                      </p>
                    </div>
                  </div>
                  {currentUsage > newLimit && (
                    <div className="rounded-md bg-destructive/10 border border-destructive/20 p-2.5 mt-2">
                      <p className="text-sm text-destructive font-medium flex items-start gap-2">
                        <span className="shrink-0">⚠️</span>
                        <span>Warning: User has imported {currentUsage} leads this month, which exceeds the new limit of {newLimit}</span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-3 flex-col-reverse sm:flex-row sm:justify-end">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)} 
            disabled={isLoading}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAssignSubscription} 
            disabled={isLoading || !reason.trim() || selectedTier === currentTier}
            className="w-full sm:w-auto"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Updating...
              </span>
            ) : (
              "Update Subscription"
            )}
          </Button>
        </DialogFooter>
        {!isLoading && selectedTier === currentTier && (
          <p className="text-xs text-muted-foreground text-center sm:text-left px-6 pb-4">
            Please select a different tier to enable update
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
